import { User, AuthUser, Permission, UserProfile, Role } from '@/types/auth';

/**
 * Converts a UserProfile (from backend) to AuthUser (for context)
 */
export function userProfileToAuthUser(profile: UserProfile): AuthUser {
  if (!profile) {
    // Defensive: fallback to a default SYSTEM_ADMIN user if profile is missing
    return {
      id: '',
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      isActive: true,
      role: {
        id: '',
        name: 'SYSTEM_ADMIN',
        code: 'SYSTEM_ADMIN',
        isSystem: true,
        permissions: [],
      },
      createdAt: '',
      updatedAt: '',
    };
  }
  let role = profile.role;
  if (!role) {
    // Default fallback role if missing from backend
    role = {
      id: '',
      name: 'SYSTEM_ADMIN',
      code: 'SYSTEM_ADMIN',
      isSystem: true,
      permissions: [],
    };
  } else if (typeof role === 'string') {
    role = {
      id: '',
      name: role,
      code: role,
      isSystem: false,
      permissions: [],
    };
  }
  return {
    ...profile,
    role: role as Role,
  };
}


/**
 * Safely gets user data from localStorage
 */
export const getUser = (): User | null => {
  if (typeof window === 'undefined') return null;
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

/**
 * Safely sets user data to localStorage
 */
export const setUser = (user: User | null): void => {
  if (typeof window === 'undefined') return;
  
  if (!user) {
    localStorage.removeItem('user');
    return;
  }

  // Ensure we're not storing sensitive data
  const { 
    password, 
    passwordResetToken, 
    passwordResetExpires, 
    mfaSecret, 
    ...safeUser 
  } = user as any; // Using type assertion since these fields might exist at runtime
  
  // Ensure role is properly typed and permissions are preserved
  const userWithRole: AuthUser = {
    ...safeUser,
    role: (() => {
      // If role is a string, create a default role object
      if (typeof safeUser.role === 'string') {
        return { 
          id: '', 
          name: safeUser.role, 
          code: safeUser.role, 
          isSystem: false, 
          permissions: [] 
        };
      }
      
      // If role is an object, ensure it has the permissions array
      return {
        ...safeUser.role,
        permissions: safeUser.role.permissions || []
      };
    })()
  };

  localStorage.setItem('user', JSON.stringify(userWithRole));
};

/**
 * Checks if user has a specific permission
 */
export const hasPermission = (permissionCode: string, user?: User | null): boolean => {
  const currentUser = user || getUser();
  if (!currentUser) return false;
  
  const role = currentUser.role;
  if (typeof role === 'string') return false; // If role is just a string, can't check permissions
  
  return role.permissions?.some(
    (perm: Permission) => perm.code === permissionCode || perm.code === '*'
  ) || false;
};

/**
 * Checks if user has any of the specified permissions
 */
export const hasAnyPermission = (permissionCodes: string[], user?: User | null): boolean => {
  return permissionCodes.some(code => hasPermission(code, user));
};

/**
 * Gets all permissions for the current user
 */
export const getUserPermissions = (user?: User | null): Permission[] => {
  const currentUser = user || getUser();
  if (!currentUser || typeof currentUser.role === 'string') return [];
  
  return currentUser.role.permissions || [];
};

/**
 * Clears user data from localStorage
 */
export const clearUser = (): void => {
  if (typeof window === 'undefined') return;
  localStorage.removeItem('user');
};
